import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BookOpen, X, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// 📖 "From the POG BLOG" peek — community discovery on the map.
export const BlogPeek = ({ posts, visible, onDismiss }) => {
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="blog-peek-card"
          initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 24 }}
          className="absolute right-3 sm:right-5 bottom-[calc(9rem+env(safe-area-inset-bottom))] z-[1140] w-60 sm:w-64 glass-strong border border-violet-400/30 rounded-2xl shadow-xl overflow-hidden [@media(max-height:480px)]:hidden"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/50">
            <button data-testid="blog-peek-header" onClick={() => navigate("/blog")} className="flex items-center gap-1.5 min-w-0 text-violet-200 hover:text-white">
              <BookOpen className="w-4 h-4 shrink-0 text-violet-400" />
              <span className="font-heading font-bold text-xs uppercase tracking-wider truncate">From the POG BLOG</span>
            </button>
            <button
              data-testid="blog-peek-dismiss"
              onClick={onDismiss}
              className="shrink-0 w-6 h-6 rounded-full hover:bg-slate-700/60 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5 text-slate-300" />
            </button>
          </div>
          <div className="divide-y divide-slate-800/60">
            {posts.map((p) => {
              const col = p.audience === "drivers" ? "text-emerald-300" : p.audience === "merchants" ? "text-amber-300" : "text-sky-300";
              return (
                <button
                  key={p.id}
                  data-testid="blog-peek-item"
                  onClick={() => navigate(`/blog/${p.slug}`)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-slate-800/50 transition-colors"
                >
                  {p.cover_url ? (
                    <img src={`${BACKEND_URL}${p.cover_url}`} alt={`AIR.POG blog: ${p.title || "post cover"}`} className="w-10 h-10 rounded-lg object-cover shrink-0 border border-slate-700/60" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-violet-300" /></div>
                  )}
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-[13px] leading-tight text-white line-clamp-2">{p.title}</p>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${col}`}>{p.audience === "everyone" ? "For everyone" : `For ${p.audience}`}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <button data-testid="blog-peek-all" onClick={() => navigate("/blog")} className="w-full flex items-center justify-center gap-1 py-2 text-xs font-heading font-bold text-violet-300 hover:bg-violet-500/10 transition-colors border-t border-slate-800/60">
            Read the POG BLOG <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// 👆 One-time coach mark — fades in, tap to dismiss.
export const CoachMark = ({ show, onDismiss }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        key="coach"
        initial={{ opacity: 0, y: 10, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.35 }}
        className="absolute inset-0 z-[1003] flex items-center justify-center pointer-events-none"
      >
        <motion.button
          data-testid="coach-mark"
          onClick={onDismiss}
          animate={{ y: [0, -7, 0] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          className="pointer-events-auto flex items-center gap-2.5 rounded-full pl-3 pr-4 py-2.5 bg-slate-900/90 backdrop-blur-md ring-1 ring-white/15 shadow-2xl shadow-indigo-500/30"
        >
          <span className="text-2xl leading-none">👆</span>
          <span className="font-heading font-bold text-sm text-white">Tap a pin to grab the deal!</span>
        </motion.button>
      </motion.div>
    )}
  </AnimatePresence>
);
